// Lankytojų elgsenos sekimo klientas — žr. src/backend/routes/analytics.js ir
// /admin/statistika (AdminAnalytics.jsx). Grynos funkcijos (ne React), kad jas
// būtų galima kviesti tiek iš hook'ų (useAnalyticsPageviews), tiek tiesiogiai iš
// AuthContext.jsx (login/register/logout įvykiams) be papildomo konteksto.
//
// visitorId (localStorage) — ilgalaikis, identifikuoja NARŠYKLĘ/ĮRENGINĮ per
// visus apsilankymus (net anoniminius) — leidžia atskirti grįžtantį lankytoją
// nuo naujo.
// sessionId (sessionStorage + neaktyvumo laikas) — vienas apsilankymas; jei
// lankytojas neaktyvus >30min, kitas veiksmas pradeda NAUJĄ sesiją net toje
// pačioje kortelėje (tas pats principas, kaip Google Analytics).
import { API_BASE } from '../config';

const VISITOR_KEY = 'fitit_visitor_id';
const SESSION_META_KEY = 'fitit_session_meta';
const SESSION_IDLE_MS = 30 * 60 * 1000;

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) { id = uuid(); localStorage.setItem(VISITOR_KEY, id); }
    return id;
  } catch { return uuid(); }
};

// Grąžina {sessionId, isNew} — isNew=true kai reikia siųsti /session (nauja
// sesija arba grįžtama po >30min neaktyvumo).
export const getSessionInfo = () => {
  const now = Date.now();
  try {
    const raw = sessionStorage.getItem(SESSION_META_KEY);
    if (raw) {
      const meta = JSON.parse(raw);
      if (meta.sessionId && now - meta.lastActivity < SESSION_IDLE_MS) {
        sessionStorage.setItem(SESSION_META_KEY, JSON.stringify({ sessionId: meta.sessionId, lastActivity: now }));
        return { sessionId: meta.sessionId, isNew: false };
      }
    }
  } catch { /* sessionStorage unavailable (private mode etc.) — treat as new each time */ }
  const sessionId = uuid();
  try { sessionStorage.setItem(SESSION_META_KEY, JSON.stringify({ sessionId, lastActivity: now })); } catch {}
  return { sessionId, isNew: true };
};

const touchSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_META_KEY);
    if (raw) {
      const meta = JSON.parse(raw);
      sessionStorage.setItem(SESSION_META_KEY, JSON.stringify({ ...meta, lastActivity: Date.now() }));
    }
  } catch {}
};

const parseUtm = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return { utmSource: p.get('utm_source') || '', utmMedium: p.get('utm_medium') || '', utmCampaign: p.get('utm_campaign') || '' };
  } catch { return { utmSource: '', utmMedium: '', utmCampaign: '' }; }
};

// Siunčiama VIENĄ kartą per sesiją (isNew) IR pakartotinai po login/register
// (kad sesija "pažymėtų" jau žinomą vartotoją — žr. backend komentarą).
export const postAnalyticsSession = () => {
  const visitorId = getVisitorId();
  const { sessionId, isNew } = getSessionInfo();
  const body = {
    sessionId, visitorId,
    landingPath: isNew ? (window.location.pathname + window.location.search) : undefined,
    referrer: isNew ? (document.referrer || '') : undefined,
    ...(isNew ? parseUtm() : {}),
    userAgent: navigator.userAgent, language: navigator.language,
    screenWidth: window.screen?.width || 0, screenHeight: window.screen?.height || 0,
  };
  fetch(`${API_BASE}/analytics/session`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => {});
  return { visitorId, sessionId };
};

// Grąžina Promise<eventId|null> — eventId reikalingas pageview trukmei vėliau
// pranešti (žr. sendPageviewDuration).
// /konfiguratorius apskritai nesekamas — čia (ne tik useAnalytics.js pageview/
// click sekimuose) sąmoningai patikrinama DAR KARTĄ, nes Layout.jsx/Builder3D.jsx
// papildomai kviečia trackEvent tiesiogiai (custom įvykiai: žingsnio keitimas,
// baldo kūrimas ir t.t.) — vienas patikrinimas čia, šaltinyje, apsaugo VISUS
// esamus IR būsimus kvietimus vienoje vietoje (žr. vartotojo prašymą "isimk
// statistikos rinkima is /konfiguratorius puslapio").
export const trackEvent = async (type, { path, label, meta } = {}) => {
  if (window.location.pathname.startsWith('/konfiguratorius')) return null;
  touchSession();
  const visitorId = getVisitorId();
  const { sessionId } = getSessionInfo();
  try {
    const res = await fetch(`${API_BASE}/analytics/event`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, visitorId, type, path: path || window.location.pathname, label: label || '', meta: meta || {} }),
    });
    const data = await res.json().catch(() => null);
    return data?.id || null;
  } catch { return null; }
};

// sendBeacon — vienintelis būdas, garantuotai pasiekiantis serverį PAČIU
// puslapio uždarymo/išvykimo momentu (fetch tuo metu dažnai nutraukiamas
// naršyklės prieš gaunant atsakymą).
export const sendPageviewDuration = (eventId, durationMs) => {
  if (!eventId || durationMs < 0) return;
  const url = `${API_BASE}/analytics/event/${eventId}/duration`;
  const body = JSON.stringify({ durationMs });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
  } catch {}
  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
};

// Geriausios pastangos etiketė paspaustam elementui — mygtuko/nuorodos tekstas,
// aria-label, arba tag+klasė, jei teksto nėra (pvz. vien-ikonos mygtukas).
const describeElement = (el) => {
  const text = (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 80);
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.slice(0, 80);
  if (text) return text;
  const title = el.getAttribute('title');
  if (title) return title.slice(0, 80);
  return `${el.tagName.toLowerCase()}${el.className ? '.' + String(el.className).split(' ')[0] : ''}`;
};

// Sekami TIK sąveikaujantys elementai (mygtukai/nuorodos/ir pan.) — ne kiekvienas
// paspaudimas bet kur (pvz. WebGL canvas viduje esantis 3D pasukimas/vilkimas
// NĖRA "paspaudimas meniu punkto" prasme ir sukeltų per daug triukšmo).
const CLICK_SELECTOR = 'button, a, input[type="button"], input[type="submit"], [role="button"], [role="link"], [role="menuitem"], [role="tab"]';

export const initGlobalClickTracking = () => {
  const handler = (e) => {
    // /konfiguratorius nesekamas apskritai — tankiai sąveikaujantis 3D
    // konfigūratorius sukeldavo daug paspaudimų per trumpą laiką (žr.
    // /admin/bugs dėl sulėtėjimo ir vartotojo prašymo "isimk statistikos
    // rinkima is /konfiguratorius puslapio"). Tikrinama PAČIU paspaudimo
    // momentu (ne vien kabinimo metu), nes tai SPA — maršrutas gali
    // pasikeisti be puslapio perkrovimo.
    if (window.location.pathname.startsWith('/konfiguratorius')) return;
    const target = e.target?.closest?.(CLICK_SELECTOR);
    if (!target) return;
    trackEvent('click', { label: describeElement(target), meta: { tag: target.tagName.toLowerCase() } });
  };
  document.addEventListener('click', handler, { capture: true, passive: true });
  return () => document.removeEventListener('click', handler, { capture: true });
};
